import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import { formatEnumValue } from '../../../../../../../util/text';
import {
  isEligibleForShutterbugFreeTrial,
  SEARCH_SUBSCRIPTION_PLAN,
} from '../../../PhotoSearchCheckout/helpers';
import PillButtonGroup from '../../../../../../../components/global/PillButtonGroup';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import Button from '../../../../../../../components/global/Button';
import SearchSubscriptionPlanOverview from '../SearchSubscriptionPlanOverview';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../../../components/global/StripeCheckout';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../../constants/account-referrals';

const propTypes = {
  className: PropTypes.string,
  searchSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      plan: PropTypes.string.isRequired,
      days: PropTypes.number.isRequired,
      price: PropTypes.shape({
        value: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  selectedSearchItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onCheckout: PropTypes.func.isRequired,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    search_subscription_free_trial_enabled: PropTypes.bool.isRequired,
  }),
  submitting: PropTypes.bool,
  activeSearchSubscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billing_period: PropTypes.string.isRequired,
    payment_processor: PropTypes.string.isRequired,
    plan: PropTypes.string.isRequired,
    expires_at: PropTypes.string.isRequired,
    trial_days: PropTypes.number,
  }).isRequired,
};

const PhotoSearchSubscriptionSelection = ({
  className,
  searchSubscriptionItems,
  selectedSearchItem,
  onCheckout,
  account,
  submitting,
  activeSearchSubscription,
}) => {
  const classes = classNames({
    [className]: !!className,
  });
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(
    SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS,
  );
  const openModal = useOpenModal();

  useEffect(() => {
    if (activeSearchSubscription) {
      setSelectedBillingPeriod(
        activeSearchSubscription.billing_period === 'MONTHLY'
          ? 'monthly'
          : 'annual',
      );
    }
  }, [activeSearchSubscription]);

  const availableSearchSubscriptionItems = searchSubscriptionItems
    ?.filter(searchSubscriptionItem =>
      selectedBillingPeriod === 'monthly'
        ? searchSubscriptionItem.days < 365
        : searchSubscriptionItem.days >= 365,
    )
    .sort((a, b) => {
      // Monthly first
      if (a.days < b.days) {
        return -1;
      }

      if (a.days > b.days) {
        return 1;
      }

      return 0;
    });

  const selectedSearchSubscriptionItem = availableSearchSubscriptionItems.find(
    searchSubscriptionItem => searchSubscriptionItem.plan === selectedPlan,
  );

  return (
    <div className={classes}>
      <div className="d-flex m-auto">
        <PillButtonGroup
          block
          className="photo-search-upsell-billing-period-selection mb-3"
        >
          <button
            className={classNames({
              'photo-search-upsell-billing-period-selection-item': true,
              active: selectedBillingPeriod === 'monthly',
            })}
            onClick={() => setSelectedBillingPeriod('monthly')}
          >
            Monthly
          </button>
          <button
            className={classNames({
              'photo-search-upsell-billing-period-selection-item': true,
              active: selectedBillingPeriod === 'annual',
            })}
            onClick={() => setSelectedBillingPeriod('annual')}
          >
            Annual
          </button>
        </PillButtonGroup>
      </div>

      <div className="photo-search-upsell-plan-options">
        {availableSearchSubscriptionItems.map(searchSubscriptionItem => {
          return (
            <SearchSubscriptionPlanOverview
              key={searchSubscriptionItem.id}
              account={account}
              searchSubscriptionItem={searchSubscriptionItem}
              onSelected={() => setSelectedPlan(searchSubscriptionItem.plan)}
              selected={
                activeSearchSubscription
                  ? searchSubscriptionItem.plan ===
                      activeSearchSubscription.plan &&
                    searchSubscriptionItem.billing_period ===
                      activeSearchSubscription.billing_period
                  : searchSubscriptionItem.plan === selectedPlan
              }
              activeSearchSubscription={activeSearchSubscription}
              onCheckout={onCheckout}
              submitting={submitting}
            />
          );
        })}
      </div>

      {selectedPlan && !activeSearchSubscription && (
        <div className="photo-search-upsell-checkout photo-search-subscription-selection-buttons">
          {selectedSearchSubscriptionItem &&
            (() => {
              const isMonthly = selectedSearchSubscriptionItem.days < 365;
              const [name, plan] = formatEnumValue(
                selectedSearchSubscriptionItem.plan,
              ).split(' ');
              const title = isEligibleForShutterbugFreeTrial(account)
                ? `${name} ${plan} + free trial`
                : `${name} ${plan}`;
              const description = isEligibleForShutterbugFreeTrial(account)
                ? `$${formatPriceForDisplay(
                    selectedSearchSubscriptionItem?.price.value,
                  )} will be charged on ${moment()
                    .add(30, 'days')
                    .format('MM/DD')}`
                : 'Checkout';
              const panelLabel = isEligibleForShutterbugFreeTrial(account)
                ? 'Subscribe'
                : `Pay $${formatPriceForDisplay(
                    selectedSearchSubscriptionItem?.price.value,
                  )}`;

              return (
                <Button
                  onClick={() => {
                    openModal(STRIPE_CHECKOUT_MODAL, {
                      title: <h3 className="text-center">{title}</h3>,
                      description: <p className="text-center">{description}</p>,
                      buttonLabel: panelLabel,
                      accountReferralTypes: [
                        ACCOUNT_REFERRAL_TYPE.SHUTTERBUG_PROMO,
                        ACCOUNT_REFERRAL_TYPE.TIER_1_SHUTTERBUG_PROMO,
                      ],
                      onSuccess: token =>
                        onCheckout({
                          searchSubscriptionItemId:
                            selectedSearchSubscriptionItem.id,
                          token,
                        }),
                      defaultName: account?.name ?? '',
                      defaultEmail: account?.email_address ?? '',
                    });
                  }}
                  btnStyle={
                    selectedSearchItem?.id ===
                      selectedSearchSubscriptionItem.id &&
                    !!selectedSearchItem.plan
                      ? undefined
                      : 'yellow'
                  }
                  disabled={submitting}
                >
                  {submitting ? (
                    'Processing your request...'
                  ) : (
                    <Fragment>
                      $
                      {formatPriceForDisplay(
                        selectedSearchSubscriptionItem.price.value,
                      )}{' '}
                      / {isMonthly ? 'month' : 'year'}
                      {isEligibleForShutterbugFreeTrial(account) && (
                        <span>30 day FREE trial</span>
                      )}
                    </Fragment>
                  )}
                </Button>
              );
            })()}
        </div>
      )}
    </div>
  );
};

PhotoSearchSubscriptionSelection.propTypes = propTypes;

export default PhotoSearchSubscriptionSelection;
